import { FC, useEffect, useRef } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import { FollowUpForm, SavedActivityInputPreview } from '../../lib/followUp';
import { activitiesContentFingerprint } from '../../lib/mailSequence';
import ScheduleMailResourcesSection from './ScheduleMailResourcesSection';

interface Props {
  activities: SavedActivityInputPreview[];
  onChange: (activities: SavedActivityInputPreview[]) => void;
}

const StepCustomResourcesEditor: FC<Props> = ({ activities, onChange }) => {
  const {
    register,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<FollowUpForm>({
    defaultValues: {
      subject: '',
      patientsOrTags: [],
      note: '',
      providerVoiceNoteId: null,
      activities,
    },
  });

  const { remove, prepend, update } = useFieldArray({
    control,
    name: 'activities',
  });

  // useWatch (unlike render-time watch) returns a fresh reference whenever
  // any nested field changes, so typed provider notes retrigger the sync.
  const watchedActivities = useWatch({ control, name: 'activities' }) ?? [];
  // Content-aware fingerprints so provider-note / completability / signer
  // edits sync between this form and the parent's stepActivitiesState.
  const lastExternalFp = useRef(activitiesContentFingerprint(activities));
  const lastEmittedFp = useRef(activitiesContentFingerprint(activities));

  useEffect(() => {
    const fp = activitiesContentFingerprint(activities);
    if (fp === lastExternalFp.current) return;
    lastExternalFp.current = fp;
    lastEmittedFp.current = fp;
    setValue('activities', activities);
  }, [activities, setValue]);

  useEffect(() => {
    const fp = activitiesContentFingerprint(watchedActivities);
    if (fp === lastEmittedFp.current) return;
    lastEmittedFp.current = fp;
    lastExternalFp.current = fp;
    onChange(watchedActivities);
  }, [watchedActivities, onChange]);

  return (
    <ScheduleMailResourcesSection
      activities={watchedActivities}
      register={register}
      watch={watch}
      setValue={setValue}
      errors={errors}
      remove={remove}
      update={update}
      prepend={prepend}
      compactLayout
      hideSectionLabel
    />
  );
};

export default StepCustomResourcesEditor;
